import { useEffect, useRef, useState } from 'react';

export default function ItemCatalogSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const scrollToCTA = () => {
    const ctaSection = document.getElementById('contact');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const items = [
    {
      title: '金・宝石類',
      icon: 'ri-vip-diamond-line',
      priceRange: '10,000円〜',
      badge: '高価買取',
      description: '金・プラチナ・ダイヤモンド',
      gradient: 'from-yellow-500 to-amber-600',
      image: 'https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/2a33b45be7d8a89fe8a0bcedbca90976.png',
    },
    {
      title: '高級時計',
      icon: 'ri-time-line',
      priceRange: '50,000円〜',
      badge: '高価買取',
      description: 'ロレックス・オメガ等',
      gradient: 'from-slate-600 to-slate-800',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/8d24ce5d-0a07-41eb-ac6a-62fd3b077ff6_250150.png?v=8cc95560bfebde05390b67a041f8b03e',
    },
    {
      title: 'ブランドバッグ',
      icon: 'ri-handbag-line',
      priceRange: '30,000円〜',
      badge: '高価買取',
      description: 'ヴィトン・エルメス等',
      gradient: 'from-rose-500 to-pink-600',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/a86b2397-d55a-42c1-b260-9a09d05cab94_250150.png?v=ba470a70875e5d64b7097d75d8f67d75',
    },
    {
      title: '骨董品',
      icon: 'ri-ancient-pavilion-line',
      priceRange: '20,000円〜',
      badge: '高価買取',
      description: '茶道具・掛け軸・陶磁器',
      gradient: 'from-amber-700 to-orange-800',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/ec34c739-e7b2-4c8b-b66c-6b8b8edaf295_250150.png?v=468798df5d5389d1f65984dcf74ab9a9',
    },
    {
      title: '大型家具',
      icon: 'ri-sofa-line',
      priceRange: '5,000円〜',
      badge: '処分込み',
      description: 'タンス・食器棚・ソファ',
      gradient: 'from-[#00A58E] to-teal-700',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/a9705432-9b97-4692-bb10-74b6ea80d3f8_250150.png?v=a760b5a4a8beb79979d2906cac366a17',
    },
    {
      title: '家電製品',
      icon: 'ri-tv-2-line',
      priceRange: '3,000円〜',
      badge: '処分込み',
      description: 'テレビ・冷蔵庫・洗濯機',
      gradient: 'from-cyan-500 to-blue-600',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/d7104d8e-f8ec-4497-beff-69ea338314d1_250150.png?v=bd126ae172a86f5a8b477314c3f9a673',
    },
    {
      title: '切手・コイン',
      icon: 'ri-copper-coin-line',
      priceRange: '5,000円〜',
      badge: '高価買取',
      description: '記念切手・古銭・メダル',
      gradient: 'from-orange-500 to-red-600',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/f8065bf3-d0db-4f61-9f0e-ed203167ca91_250150.png?v=d6b1d1f69806e35149b9c66a9c7138e1',
    },
    {
      title: '着物・和装品',
      icon: 'ri-shirt-line',
      priceRange: '10,000円〜',
      badge: '高価買取',
      description: '訪問着・振袖・帯',
      gradient: 'from-purple-500 to-indigo-600',
      image: 'https://storage.readdy-site.link/project_files/3b297a6c-bcf3-44cd-a2dd-4d9f6103258a/ed27312d-b4b4-4fae-940a-3bee2fb85c68_250150.png?v=330bbe20bbe5d1d6b4b7ba107fbc7a42',
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="item-catalog"
      className="py-20 px-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdfbf7 0%, #f5f1e8 100%)',
      }}
    >
      {/* 和紙テクスチャ背景 */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* 木目調アクセント */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-700 via-amber-600 to-amber-700 opacity-30"></div>
      <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-700 via-amber-600 to-amber-700 opacity-30"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* セクションヘッダー */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-[#00A58E] font-bold text-sm tracking-widest uppercase border-b-2 border-[#00A58E] pb-1">
              Item Catalog
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            買取品目カタログ
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            金・宝石から大型家具まで、幅広く対応いたします。<br />
            「これは売れるかな？」と思ったら、まずはお気軽にご相談ください。
          </p>
        </div>

        {/* 品目グリッド（4×2） */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {items.map((item, index) => (
            <div
              key={index}
              onClick={scrollToCTA}
              className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform cursor-pointer group ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{
                transitionDelay: `${index * 100}ms`,
              }}
            >
              {/* アイコン部分 */}
              <div className={`relative h-40 bg-gradient-to-br ${item.gradient} flex items-center justify-center overflow-hidden`}>
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-20 h-20 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-5xl text-white`}></i>
                  </div>
                )}
                {/* バッジ */}
                <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  {item.badge}
                </div>
              </div>

              {/* コンテンツ部分 */}
              <div className="p-5">
                <h3 className="text-xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                  {item.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-[#00A58E]"></i>
                    <span className="text-[#00A58E] font-bold text-lg">{item.priceRange}</span>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#00A58E] group-hover:translate-x-1 transition-all"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 注意書き */}
        <div className="text-center mb-12">
          <div className="inline-block bg-white/80 backdrop-blur-sm rounded-2xl shadow-md px-8 py-4 border border-amber-200">
            <div className="flex items-center justify-center gap-3 text-sm text-gray-600">
              <i className="ri-information-line text-[#00A58E] text-xl"></i>
              <p>
                <strong className="text-gray-800">参考価格帯です。</strong>
                実際の買取価格は、品物の状態・年代・需要により変動いたします。
              </p>
            </div>
          </div>
        </div>

        {/* CTA部分 */}
        <div className="text-center">
          <div className="inline-block bg-gradient-to-r from-[#00A58E] to-teal-600 rounded-3xl shadow-2xl px-12 py-10 max-w-4xl">
            <div className="flex items-center justify-center mb-4">
              <i className="ri-question-answer-line text-5xl text-white"></i>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              お品物の価値、<br className="md:hidden" />まずはご相談ください
            </h3>
            <p className="text-white/90 text-lg leading-relaxed mb-8">
              「これは売れるのかな？」「価値があるのかわからない」<br />
              そんな疑問も、吉田鑑定士が丁寧にお答えいたします。<br />
              <strong className="text-amber-300">査定・相談は完全無料</strong>です。
            </p>
            <button
              onClick={scrollToCTA}
              className="group inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 text-white px-10 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-orange-500/50 hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-phone-line text-2xl"></i>
              <span>無料査定を申し込む</span>
              <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
            </button>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-amber-300"></i>
                <span>出張費無料</span>
              </div>
              <span className="text-white/40">|</span>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-amber-300"></i>
                <span>最短即日</span>
              </div>
              <span className="text-white/40">|</span>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-amber-300"></i>
                <span>しつこい営業なし</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}